/**
 * LE RULEBOOK DES TRANSITIONS.
 *
 * L'interface ne sait jamais « après X vient Y » : elle demande ici quelle est la prochaine action
 * possible, comment elle s'appelle, et ce qu'elle exige. Ajouter une étape au parcours se fait donc dans ce
 * seul fichier, sans toucher un écran.
 *
 * ⚠️ CE RULEBOOK EXISTE EN DEUX EXEMPLAIRES. Celui-ci pilote l'affichage ; une copie compacte vit côté
 * serveur dans `functions/api/[[path]].js` (chercher PIPELINE_TRANSITIONS) et fait AUTORITÉ. C'est elle qui
 * refuse une transition illégale ou un document manquant. Se fier au masquage d'un bouton pour interdire
 * une action serait la faille que le cadre interdit (section VI.3) : le front est en TypeScript, le back en
 * JavaScript, aucun code n'est partageable entre les deux dans cette base de code, donc **toute
 * modification ici doit être reportée là-bas.**
 */

import { PipelineStage, PIPELINE_STAGE_SKIPPABLE, PIPELINE_STAGE_ORDER, PIPELINE_STAGE_LABELS, stageRank } from './pipelineStages';

/** Nature du justificatif exigé — sert de `kind` sur la ligne `event_documents` créée par la transition. */
export type PipelineDocumentKind = 'plaquette' | 'devis' | 'devis_signe';

export interface PipelineTransition {
    /** Ce que le bouton annonce. Un verbe d'action, jamais le nom de l'étape d'arrivée. */
    actionLabel: string;
    nextStage: PipelineStage;
    /** Quand c'est vrai, la transition est refusée tant qu'aucun fichier n'accompagne la demande. */
    requiresDocument: boolean;
    documentKind?: PipelineDocumentKind;
    /** Champs du dossier à renseigner en même temps, sans quoi la transition est refusée. */
    requiresFields?: { name: string; label: string; type: 'text' | 'number' }[];
    /** Explication affichée dans la modale, pour que l'utilisateur sache ce qu'il engage. */
    hint?: string;
    /** L'étape ouvre un écran dédié au lieu de la modale habituelle (création de la privatisation). */
    opensForm?: 'creer_privatisation';
    /** Le formulaire du brief s'affiche DANS cette modale (2026-08-31) : plus besoin de renvoyer vers
     *  l'onglet Brief pour le remplir — un seul geste enregistre les réponses ET fait avancer le dossier. */
    embedsBrief?: boolean;
    /** Vrai quand l'étape est PASSÉE et non accomplie — le serveur l'exige pour ne rien horodater. */
    skip?: boolean;
}

/** Ce que le dossier a déjà, et qui change ce que l'étape suivante réclame. */
export interface PipelineContexte {
    /** Le client a déposé son exemplaire signé depuis son lien : plus rien à téléverser. */
    hasSignedDevis?: boolean;
}

/**
 * L'action qui fait avancer d'un cran depuis l'étape courante.
 * `null` = plus rien à faire automatiquement (clôturée, perdue, annulée), ou étape inconnue.
 *
 * Le contexte est facultatif : sans lui, le parcours se comporte comme avant l'arrivée du lien public.
 */
export const getNextTransition = (stage?: string | null, contexte?: PipelineContexte): PipelineTransition | null => {
    switch (stage) {
        case 'demande_recue':
            return {
                actionLabel: 'Envoyer la plaquette',
                nextStage: 'plaquette_envoyee',
                requiresDocument: true,
                documentKind: 'plaquette',
                hint: "La plaquette part avec votre message et un bouton vers la Démarche de votre choix. Le client y répond et prend son rendez-vous — vous n'avez rien à ressaisir.",
            };
        case 'plaquette_envoyee':
            return {
                actionLabel: 'Enregistrer le retour client',
                nextStage: 'retour_client',
                requiresDocument: false,
                requiresFields: [{ name: 'formule_retenue', label: 'Formule qui intéresse le client', type: 'text' }],
                hint: "Notez la formule retenue telle que le client l'a exprimée. Si sa réponse au questionnaire est déjà arrivée, elle est signalée sur la fiche.",
            };
        case 'retour_client':
            return {
                actionLabel: 'Enregistrer le brief',
                nextStage: 'brief_collecte',
                requiresDocument: false,
                embedsBrief: true,
                hint: "Le brief regroupe ce qui a été récolté au rendez-vous et dans les échanges qui ont suivi : déroulé de la soirée, prestataires, contraintes. C'est ce qui permet de chiffrer.",
            };
        case 'brief_collecte':
            return {
                actionLabel: 'Envoyer le devis',
                nextStage: 'devis_envoye',
                requiresDocument: true,
                documentKind: 'devis',
                requiresFields: [{ name: 'devis_amount_ttc', label: 'Montant du devis TTC (€)', type: 'number' }],
                hint: "Le devis est établi dans votre logiciel habituel : la plateforme n'en génère aucun, elle conserve celui que vous avez produit. Le montant sert au prévisionnel commercial.",
            };
        case 'devis_envoye': {
            // Le devis signé peut déjà être arrivé par le lien public : le réclamer une seconde fois
            // obligerait le commercial à re-déposer un fichier qu'il vient d'ouvrir depuis la fiche.
            // Le serveur applique exactement la même règle (chercher `devis_signed_document_id` dans
            // POST /suivi-privatisation/:dossierId/transition) — c'est lui qui fait autorité.
            const dejaSigne = !!contexte?.hasSignedDevis;
            return {
                actionLabel: dejaSigne ? 'Valider le devis signé' : 'Téléverser le devis signé',
                nextStage: 'devis_signe',
                requiresDocument: !dejaSigne,
                documentKind: 'devis_signe',
                hint: dejaSigne
                    ? "Le client a déposé son exemplaire signé depuis son lien. Ouvrez son aperçu depuis le bandeau ci-dessous avant de valider : c'est le jalon qui engage l'établissement, et rien ne démarre avant lui."
                    : "C'est le jalon qui engage l'établissement : rien ne démarre avant lui, ni la privatisation dans Événementiel, ni les prestataires, ni les menus.",
            };
        }
        case 'devis_signe':
            return {
                actionLabel: 'Créer la privatisation',
                nextStage: 'privatisation_creee',
                requiresDocument: false,
                opensForm: 'creer_privatisation',
                hint: "Maintenant que le devis est signé, l'événement peut entrer dans Événementiel avec sa vraie date. C'est le seul moment où l'on est sûr qu'il aura lieu.",
            };
        case 'privatisation_creee':
            return {
                actionLabel: 'Prestataires bookés',
                nextStage: 'prestataires_bookes',
                requiresDocument: false,
                hint: "Le besoin a été noté à la création de la privatisation. Le booking lui-même — choisir qui vient, envoyer la demande au partenaire — se fait dans le module Extras, avec « Booker » ou « Demander à booker ». Cette étape sert à noter que c'est fait, ou à la passer si cette privatisation n'a besoin d'aucun extra.",
            };
        case 'prestataires_bookes':
            return {
                actionLabel: 'Fournisseurs bookés',
                nextStage: 'fournisseurs_bookes',
                requiresDocument: false,
                hint: "Les commandes se passent dans le module Fournisseurs. À passer si aucune commande n'est nécessaire.",
            };
        case 'fournisseurs_bookes':
            return {
                actionLabel: 'Lancer la collecte des menus',
                nextStage: 'collecte_menus',
                requiresDocument: false,
                hint: "Ouvre Menus & Convives pour cette privatisation : envoi aux convives, réponses, validation.",
            };
        case 'collecte_menus':
            return {
                actionLabel: 'Passer en Jour J',
                nextStage: 'jour_j',
                requiresDocument: false,
                hint: "Bascule le dossier en exploitation — c'est le Mode terrain, dans le module Cuisine, qui prend le relais.",
            };
        case 'jour_j':
            return {
                actionLabel: 'Clôturer la privatisation',
                nextStage: 'cloturee',
                requiresDocument: false,
                hint: "Normalement déclenchée par « Terminer le service » depuis le Mode terrain. La clôture n'envoie aucun email à personne.",
            };
        default:
            return null;
    }
};

/**
 * Les étapes qu'on peut passer sans les accomplir : toutes les privatisations n'ont pas d'extras ni de
 * commande fournisseur. Le journal garde la trace qu'elles ont été PASSÉES, pas faites — la nuance compte
 * si quelqu'un se demande six mois plus tard pourquoi aucun prestataire n'a été commandé.
 */
export const getSkipTransition = (stage?: string | null): PipelineTransition | null => {
    if (!stage || !PIPELINE_STAGE_SKIPPABLE.includes(stage as PipelineStage)) return null;
    const suivante = getNextTransition(stage);
    if (!suivante) return null;
    return {
        ...suivante,
        actionLabel: 'Passer cette étape',
        requiresDocument: false,
        requiresFields: undefined,
        opensForm: undefined,
        skip: true,
        hint: "Cette privatisation n'a pas besoin de cette étape. Elle sera notée comme passée, pas comme faite.",
    };
};

/**
 * Le devis a été envoyé hors plateforme (messagerie, courrier…) et le client l'a accepté : on déclare
 * l'acceptation sans repasser par l'envoi ni par un exemplaire signé. Disponible depuis « Brief collecté »
 * (le montant reste demandé) et « Devis envoyé ». Même règle côté serveur (chercher `horsPlateforme`
 * dans `functions/api/[[path]].js`), qui fait autorité.
 */
export const getDevisAccepteHorsPlateforme = (stage?: string | null): PipelineTransition | null => {
    if (stage !== 'brief_collecte' && stage !== 'devis_envoye') return null;
    return {
        actionLabel: 'Devis déjà accepté par le client',
        nextStage: 'devis_signe',
        requiresDocument: false,
        documentKind: 'devis_signe',
        requiresFields: stage === 'brief_collecte'
            ? [{ name: 'devis_amount_ttc', label: 'Montant du devis TTC (€)', type: 'number' }]
            : undefined,
        hint: "Le devis a été envoyé et accepté hors de la plateforme. Le dossier passe directement à « Devis signé » ; vous pouvez joindre l'exemplaire signé ou la réponse du client si vous l'avez, mais ce n'est pas obligatoire.",
    };
};

/**
 * L'étape précédente, pour le bouton discret de retour en arrière du pipeline. Emeni (2026-08-31) :
 * « je m'imaginais côté restaurant des boutons Suivant à chaque étape et Terminer, avec la possibilité de
 * revenir en arrière ». `null` = pas de précédent (première étape, issue perdue/annulée, étape inconnue).
 *
 * CAS PARTICULIER : rien depuis « Privatisation créée » — cette étape crée un vrai événement dans
 * Événementiel (pas qu'un horodatage) ; le retour en arrière n'a pas vocation à le supprimer. Même règle
 * côté serveur (chercher `PIPELINE_PREVIOUS` dans `functions/api/[[path]].js`), qui fait autorité.
 */
export const getBackTransition = (stage?: string | null): { previousStage: PipelineStage; label: string } | null => {
    if (!stage || stage === 'privatisation_creee') return null;
    const rang = stageRank(stage);
    if (rang <= 0) return null;
    const precedente = PIPELINE_STAGE_ORDER[rang - 1];
    return { previousStage: precedente, label: PIPELINE_STAGE_LABELS[precedente] };
};

/**
 * Les issues disponibles à tout moment, en plus de l'avancement nominal.
 * « Perdue » n'a de sens qu'avant la signature — après, l'affaire a été gagnée puis annulée, ce qui ne se
 * raconte pas de la même façon dans un bilan commercial.
 */
export const getAbandonTransition = (stage?: string | null): PipelineTransition | null => {
    if (!stage || stage === 'cloturee' || stage === 'perdue' || stage === 'annulee') return null;
    const signe = ['devis_signe', 'privatisation_creee', 'prestataires_bookes', 'fournisseurs_bookes', 'collecte_menus', 'jour_j'].includes(stage);
    return signe
        ? {
            actionLabel: 'Annuler la privatisation',
            nextStage: 'annulee',
            requiresDocument: false,
            requiresFields: [{ name: 'lost_reason', label: "Motif de l'annulation", type: 'text' }],
            hint: "Le devis était signé : le motif sera lu plus tard pour comprendre ce qui s'est passé.",
        }
        : {
            actionLabel: 'Marquer perdue',
            nextStage: 'perdue',
            requiresDocument: false,
            requiresFields: [{ name: 'lost_reason', label: 'Motif de la perte', type: 'text' }],
            hint: 'Le motif alimente le bilan commercial : prix, disponibilité, concurrence, sans suite…',
        };
};
